import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import {
  ColumnConfig,
  DataTable,
  DataTablePagination,
  defineTableSchema,
  useConfiguredTable,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  AddFilterTrigger,
  FilterBar,
  FilterEditor,
  useFilters,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/filters';
import { useQuery } from '@tanstack/react-query';
import { CircleAlert, UserIcon } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import service from '@Service';
import type { TicketedEventOrganizer } from '@Types/ticketedEvents';

const fallbackData: TicketedEventOrganizer[] = [];

const columnConfigs: ColumnConfig<TicketedEventOrganizer>[] = [
  {
    id: 'name',
    headerLabel: 'Name',
    accessor: (row) => row.name ?? 'N/A',
    type: 'text',
  },
  {
    id: 'email',
    headerLabel: 'Email',
    accessor: (row) => row.email ?? 'N/A',
    type: 'text',
  },
  {
    id: 'phone',
    headerLabel: 'Phone',
    accessor: (row) => row.phone ?? 'N/A',
    type: 'text',
  },
  {
    id: 'website',
    headerLabel: 'Website',
    accessor: (row) => row.website ?? 'N/A',
    type: 'text',
  },
];

export default function EventOrganizersList() {
  const navigate = useNavigate();
  const { filters, addOrUpdateFilter, removeFilter } = useFilters([]);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { columns, filters: filterDefinitions } =
    defineTableSchema<TicketedEventOrganizer>(columnConfigs);

  const { data, status } = useQuery<TicketedEventOrganizer[]>({
    queryKey: ['organizers'],
    queryFn: async () => service.getOrganizers(),
  });

  const { table } = useConfiguredTable({
    data: data ?? fallbackData,
    columns,
    filters,
    pagination: {
      pageIndex,
      pageSize,
      setPageIndex,
      setPageSize,
    },
    autoResetPageOnFilterChange: true,
  });

  return (
    <div>
      <div className="mt-2 space-y-4">
        <div className="flex h-16 w-full items-center gap-4 rounded-lg bg-zinc-100 p-4">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-200">
              <UserIcon className="h-4 w-4 text-zinc-500" />
            </div>
            <div>
              Organizers:{' '}
              <span className="font-bold">{data ? data.length : 0}</span>
            </div>
          </div>
        </div>

        <FilterBar>
          {filters.map((filterItem) => {
            const def = filterDefinitions.find(
              (definition) => definition.field === filterItem.field,
            );
            if (!def) return null;
            return (
              <FilterEditor
                key={filterItem.field}
                field={filterItem.field}
                displayName={filterItem.displayName}
                filterItem={filterItem}
                inputType={def.type}
                operators={def.operators}
                applyFilter={addOrUpdateFilter}
                removeFilter={removeFilter}
                description={def.description}
              />
            );
          })}

          <AddFilterTrigger
            availableFilters={filterDefinitions.filter(
              (def) =>
                !filters.some((filterItem) => filterItem.field === def.field),
            )}
            onAdd={(newFilter) => addOrUpdateFilter(newFilter)}
          />
        </FilterBar>

        {status === 'error' ? (
          <Alert variant="destructive" className="my-4">
            <CircleAlert />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Error loading organizers. Please try again later.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <DataTable
              table={table}
              onRowClick={(row) =>
                navigate(`/events/organizers/${row.original.id}`)
              }
              isLoading={status === 'pending'}
              skeletonRowCount={pageSize}
            />
            <DataTablePagination
              table={table}
              meta={{ total: table.getFilteredRowModel()?.rows?.length }}
              pagination={{ size: pageSize, number: pageIndex + 1 }}
              setPage={({ size, number }) => {
                setPageSize(size);
                setPageIndex(number - 1);
              }}
              setPageNumber={(pageNumber) => setPageIndex(pageNumber - 1)}
            />
          </>
        )}
      </div>
    </div>
  );
}
